import { Form, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { ECrsStav, ICrsDotaznik, crsDotaznikGet, crsDotaznikSend } from 'services/poDlt/crs';

import { EAnoNe } from 'enums/anoNe';

import anoNe from 'data/codebooks/anoNeSelect';
import crsStatusSelect from 'data/codebooks/podlt/crsStatusSelect';

import giinKodValidation from 'utils/validations/giinKodValidation';

interface ICrsDotaznikPage {
    nextStep: () => void;
    prevStep: () => void;
    zadostID: number;
    dotaznikData?: ICrsDotaznik;
    statutStep?: boolean;
    setDotaznikData: React.Dispatch<React.SetStateAction<ICrsDotaznik | undefined>>;
}

const CrsDotaznik: React.FC<ICrsDotaznikPage> = ({
    nextStep,
    prevStep,
    zadostID,
    dotaznikData,
    statutStep,
    setDotaznikData
}) => {
    const { cisStaty } = useContext(Context);
    const [form] = Form.useForm();
    const setRerender = useState<boolean>(false)[1];
    const [loading, setLoading] = useState<boolean>(false);
    const viewOnly = dotaznikData?.stav === ECrsStav.ZPRAC;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        const finishData = {
            ...dotaznikData,
            ...value,
            osobycr: value.osobycr === EAnoNe.ANO
        } as ICrsDotaznik;
        if (value.osobycr === EAnoNe.ANO && value.stat === 'CZ') {
            delete finishData.statut;
        }
        if (!value.statut?.includes('2')) {
            delete finishData.giin;
        }
        setDotaznikData(finishData);
        if ((finishData.osobycr && finishData.stat === 'CZ') || statutStep) {
            if (viewOnly) {
                nextStep();
            } else {
                setLoading(true);
                crsDotaznikSend('PUT', finishData, zadostID)
                    .then(() => nextStep())
                    .finally(() => setLoading(false));
            }
        } else {
            nextStep();
        }
    };

    useEffect(() => {
        setLoading(true);
        crsDotaznikGet(zadostID)
            .then(response => {
                setDotaznikData(response);
                form.setFieldsValue({
                    ...response,
                    osobycr:
                        typeof response?.osobycr === 'boolean'
                            ? response?.osobycr
                                ? EAnoNe.ANO
                                : EAnoNe.NE
                            : undefined
                });
            })
            .finally(() => setLoading(false));
        // form.setFieldsValue(dotaznikData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const rerender = () => {
        setRerender(val => !val);
    };

    return (
        <LoadingWrapper loading={loading}>
            <FormWrapper outForm={form} onFinish={onFinish} preserve={false}>
                <SelectComponent
                    disabled={statutStep || viewOnly}
                    name="stat"
                    labelParagraph="CRS_STAT_TAX"
                    isFormItem={true}
                    defaultValue={dotaznikData?.stat}
                    data={cisStaty}
                    onChange={rerender}
                />
                <InputComponent
                    label="DIC"
                    name="dic"
                    maxLength={20}
                    infoTooltipText="CRS_DIC_TOOLTIP"
                    initialValue={dotaznikData?.dic}
                    disabled={viewOnly}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const stat = getFieldValue('stat');
                                if (!value) {
                                    return Promise.resolve();
                                }
                                if (stat === 'CZ' && !value.startsWith('CZ')) {
                                    return Promise.reject(<TranslateText>CRS_DIC_CZ_VALIDATION</TranslateText>);
                                }
                                return Promise.resolve();
                            }
                        })
                    ]}
                />
                <SelectComponent
                    disabled={statutStep || viewOnly}
                    name="osobycr"
                    labelParagraph="CRS_OSOBY_CR"
                    isFormItem={true}
                    defaultValue={
                        typeof dotaznikData?.osobycr === 'boolean'
                            ? dotaznikData?.osobycr
                                ? EAnoNe.ANO
                                : EAnoNe.NE
                            : undefined
                    }
                    data={anoNe}
                    onChange={rerender}
                />
                {statutStep && (
                    <>
                        <SelectComponent
                            disabled={viewOnly}
                            name="statut"
                            labelParagraph="CRS_STATUS"
                            isFormItem={true}
                            defaultValue={dotaznikData?.statut}
                            data={crsStatusSelect}
                            onChange={() => setRerender(pv => !pv)}
                            listHeight={290}
                        />
                        {form.getFieldValue('statut')?.includes('2') && (
                            <InputComponent
                                label="CRS_GIIN"
                                infoTooltipText="CRS_GIIN_TOOLTIP"
                                name="giin"
                                notRequired={true}
                                rules={giinKodValidation()}
                                initialValue={dotaznikData?.giin}
                                disabled={viewOnly}
                            />
                        )}
                        <Paragraph
                            text="CRS_STATUS_1_INFO"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_1A_INFO_TITLE"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_1A_INFO"
                            style={{
                                display: 'block'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_1B_INFO_TITLE"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_1B_INFO"
                            style={{
                                display: 'block'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_2_INFO_TITLE"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_2_INFO"
                            style={{
                                display: 'block'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_3_INFO"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_3B_INFO_TITLE"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_3B_INFO"
                            style={{
                                display: 'block'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_3E_TITLE"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_3E_INFO"
                            style={{
                                display: 'block'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_INFO_BLIZSIE"
                            style={{
                                marginTop: 16,
                                display: 'block'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_4_INFO_TITLE"
                            style={{
                                marginTop: 16,
                                display: 'block',
                                fontWeight: 'bold'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_4_INFO"
                            style={{
                                display: 'block'
                            }}
                        />
                        <Paragraph
                            text="CRS_STATUS_INFO_BLIZSIE"
                            style={{
                                marginTop: 16,
                                display: 'block'
                            }}
                        />
                    </>
                )}
                <Row className="button-row">
                    <ButtonComponent buttontext="ZPET" type="default" onClick={() => prevStep()} id="isinZpetBtn" />
                    <ButtonComponent buttontext="POKRACOVAT" onClick={() => form.submit()} id="crsPokracovatBtn" />
                </Row>
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default CrsDotaznik;
